// packages/core/src/learning/school-modules/macro-money.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Macro money". surface: 'school', homeSchool: 'macro-money'.
// module id 'school-macro-money' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #3a5a8c) — flag for the presentation-tuning pass.
// tier/pillar: macro worldview lens (SchoolMeta bucket
// 'macro-wealth-perspectives') → tier 'growth', pillar 'money-psychology-core'
// (per the protocol default: worldview/mindset lenses → growth /
// money-psychology-core). Same mapping as the sibling macro schools
// anti-fragility, hard-money, inequality-awareness, and keynesian-mmt. Flag for
// the presentation-tuning pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_MACRO_MONEY_MODULE: LearningModule = {
	id: 'school-macro-money',
	title: 'Macro money',
	description:
		'The lens that connects big economic forces — inflation, rates, jobs, housing — to your own numbers, so the plan survives more than one environment, without sliding into forecasting, panic, or excuses.',
	emoji: '🌐', // PLACEHOLDER — tune in presentation pass
	accentColor: '#3a5a8c', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'money-psychology-core',
	order: 0,
	surface: 'school',
	homeSchool: 'macro-money',
	lessons: [
		{
			id: 'school-macro-money-01',
			title: 'The economy enters your home',
			description:
				'Learn why Macro money sees inflation, jobs, wages, housing, and rates as forces that reach ordinary households.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['macro-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'household-macro-exposure',
				'economic-pressure',
				'personal-finance-context',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'household macro exposure',
				'economic pressure',
				'personal finance context',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Macro money starts with a blunt reminder: your money life does not happen in a sealed room. The economy enters through rent, groceries, wages, rates, insurance, job openings, and the cost of debt.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes personal discipline matters, but it is not the whole story. A person can budget carefully and still feel squeezed if prices rise faster than income, housing gets expensive, or work becomes less stable.',
				},
				{
					type: 'bullet_list',
					items: [
						'Inflation changes what cash buys',
						'Rates change debt and housing costs',
						'Job markets change income power',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating every money outcome like it came only from personal choices.',
				},
				{
					type: 'example',
					text: 'Someone keeps the same job, same habits, and same spending style. Then rent rises, car insurance jumps, groceries cost more, and raises do not keep up. Their behavior did not collapse. The environment changed around them.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that macro awareness can become an excuse if it removes all agency. The wider economy matters, but the next practical move still matters too.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens starts by comparing your Dashboard to the economic weather around you. Ask what parts of your plan depend on stable prices, stable income, or low borrowing costs.',
				},
				{
					type: 'key_takeaway',
					text: 'Macro money does not replace personal responsibility. It makes responsibility more honest.',
				},
			],
			audioScript:
				'Macro money begins with one idea: your personal money life is connected to the wider economy whether you study economics or not. You do not need to read policy reports for macro forces to affect you. They show up through rent, groceries, gas, insurance, credit card rates, mortgage rates, job openings, wages, layoffs, taxes, and public services.\n\nA lot of personal finance advice talks as if every outcome comes from individual behavior. Spend less. Save more. Work harder. Learn more. Those things can matter. Nisk should never pretend choices do not matter. But Macro money says choices happen inside an environment. If the environment changes, the same choices can produce different results.\n\nImagine someone who is doing many things right. They work full time. They track spending. They avoid huge lifestyle jumps. They are trying to build runway. Then rent rises by $250. Groceries rise. Car insurance rises. Their raise is small. Suddenly the plan feels worse, even though they did not become irresponsible. Their personal discipline stayed similar. The macro environment got heavier.\n\nThat is why this lens matters. Inflation changes what savings can buy. Interest rates change what debt costs and what housing feels like. Job markets change how much power workers have. Housing supply affects whether moving, renting, or buying feels realistic. Public policy can affect taxes, benefits, services, and demand. These are not abstract debates when they hit your bank account.\n\nThe useful part of Macro money is that it removes false shame. If the cost of living rises faster than your income, that is not automatically a character failure. If housing is much harder than it was for a previous generation, that matters. If a weak job market makes a career move harder, that matters. Seeing the context gives you a clearer picture.\n\nBut the blind spot is real. Macro awareness can become defeatism. Some people get so focused on the economy, politics, inflation, or recession fears that they stop making the moves that are still available. That is not wisdom. A bad environment changes the plan, but it does not remove the need for a plan.\n\nInside Nisk, this lesson should make you look at Dashboard differently. Your numbers are personal, but they are exposed to outside pressure. Which expenses are most sensitive to inflation? How much runway do you have if income gets shaky? What debts would become more painful if rates changed? What part of your plan assumes calm conditions?\n\nMacro money is not about predicting every storm. It is about knowing that weather exists. Personal responsibility gets stronger when it includes the conditions around you.',
			flashcards: [
				{
					id: 'school-macro-money-01-fc-1',
					role: 'main_idea',
					front: 'What does macro money notice?',
					back: 'Big forces like prices, rates, wages, jobs, and housing affect personal choices.',
				},
				{
					id: 'school-macro-money-01-fc-2',
					role: 'watch_out',
					front: 'What is the blame trap?',
					back: 'Treating every money outcome as only personal behavior.',
				},
				{
					id: 'school-macro-money-01-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Check which parts of your plan depend on stable prices, income, or rates.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your macro exposure',
			},
			revision: 1,
		},
		{
			id: 'school-macro-money-02',
			title: 'Rates move the room',
			description:
				'Learn why Macro money watches interest rates because they affect debt, housing, savings, jobs, and asset prices.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['macro-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'interest-rate-pressure',
				'borrowing-cost',
				'rate-sensitive-choices',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'interest rate pressure',
				'borrowing cost',
				'rate sensitive choices',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Interest rates can sound like banker language until they change your rent, mortgage, credit card payment, car loan, job market, or savings yield.',
				},
				{
					type: 'paragraph',
					text: 'Macro money believes rates move the room because they change the cost of borrowing and the reward for holding cash. When rates shift, households, businesses, investors, and governments all respond.',
				},
				{
					type: 'bullet_list',
					items: [
						'Higher rates can make debt heavier',
						'Lower rates can make borrowing easier',
						'Rate changes can affect jobs and housing',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking rates only matter to investors.',
				},
				{
					type: 'example',
					text: 'A higher mortgage rate can make the same home much less affordable. A higher credit card rate can make carried debt more painful. A higher savings yield can help cash earn more, but may come with a slower economy.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that rate changes are not pure good or pure bad. Higher rates may fight inflation and reward savers, but they can pressure borrowers and slow hiring. Lower rates may support borrowing and growth, but they can feed asset prices and debt risk.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a stress test. Ask how your plan changes if borrowing costs rise, housing stays expensive, or income weakens.',
				},
				{
					type: 'key_takeaway',
					text: 'Interest rates are not background noise. They change the price of time, debt, and patience.',
				},
			],
			audioScript:
				'Interest rates can feel distant until they touch your life. Then they become very real. A rate change can affect whether a mortgage payment fits, whether a car loan hurts, whether credit card debt grows faster, whether businesses hire, whether savings accounts pay more, and whether investors feel bold or cautious. Macro money pays attention to rates because rates move the room.\n\nAt the simplest level, an interest rate is the price of borrowing money and the reward for lending or holding certain kinds of cash. When rates are low, borrowing can feel easier. Homes may look more affordable. Businesses may expand. Asset prices may rise because future returns are discounted differently and money is cheaper. When rates are higher, borrowing gets more expensive. Debt payments can bite harder. Home affordability can fall. Businesses may slow hiring or investment. Savers may earn more on cash, but the economy may feel tighter.\n\nThat is why rates are not just an investor topic. A normal household can feel them quickly. If someone carries credit card debt, a high rate can make the balance harder to escape. If someone wants to buy a home, the same purchase price can become much less affordable when the mortgage rate rises. If a company faces higher borrowing costs, it may delay expansion or hiring. If a person has cash savings, higher rates may finally make that cash earn something. The same macro force can help one part of life and hurt another.\n\nThis lens is useful because it teaches tradeoff. Higher rates are not automatically bad. They may help cool inflation and reward savers. Lower rates are not automatically good. They may support growth, but also encourage debt, inflate asset prices, and make housing more expensive in some markets. Macro money refuses to reduce rates to one simple emotion.\n\nHere is a practical example. A buyer looks at a home and focuses only on price. But the rate changes the monthly reality. A $350,000 home at one rate and the same home at a much higher rate can feel like two different purchases. The asset is the same. The financing environment is not. That difference can change whether the buyer has room for repairs, savings, emergencies, and life.\n\nInside Nisk, this lesson should push a stress test. What debt do you have that is rate sensitive? What housing decision depends on borrowing costs? What happens if income weakens while debt stays expensive? What happens if your savings earn more, but your job market gets softer? These questions keep the macro force connected to your real numbers.\n\nRates move the room because they change incentives. They change who feels pressure, who gets breathing room, and which choices look possible. You do not need to predict rates perfectly. You need to know whether your plan can handle the room changing.',
			flashcards: [
				{
					id: 'school-macro-money-02-fc-1',
					role: 'main_idea',
					front: 'Why do rates matter?',
					back: 'They change borrowing costs, savings rewards, housing affordability, jobs, and asset behavior.',
				},
				{
					id: 'school-macro-money-02-fc-2',
					role: 'watch_out',
					front: 'What is the rate mistake?',
					back: 'Thinking interest rates only matter to investors or banks.',
				},
				{
					id: 'school-macro-money-02-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'How your debt, housing, runway, and income respond if rates shift.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test rate pressure',
				scenarioType: 'custom',
			},
			revision: 1,
		},
		{
			id: 'school-macro-money-03',
			title: 'Prepare without predicting',
			description:
				'Learn where Macro money breaks down when awareness turns into forecasting, panic, or paralysis.',
			pillar: 'money-psychology-core',
			schoolsOfThought: ['macro-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'forecast-humility',
				'macro-preparation',
				'resilient-planning',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'forecast humility',
				'macro preparation',
				'resilient planning',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Macro money gets dangerous when it becomes a prediction game. The economy is too complex for most people to build their life around perfect forecasts.',
				},
				{
					type: 'paragraph',
					text: 'This lens works best when it helps you prepare for different conditions. Inflation may rise or cool. Jobs may stay strong or weaken. Rates may move. Housing may stay tight. Your plan should not depend on guessing everything correctly.',
				},
				{
					type: 'bullet_list',
					items: [
						'Forecasts can be wrong',
						'Buffers reduce dependence on predictions',
						'Flexible plans beat fragile certainty',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning macro awareness into panic scrolling.',
				},
				{
					type: 'example',
					text: 'Someone spends months watching recession videos and makes no practical change. Another person checks runway, reduces one fragile payment, keeps skills current, and tests income options. The second person is not predicting better. They are preparing better.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is ignoring macro entirely because forecasts are hard. You do not need perfect predictions to notice exposure and build buffers.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens ends with a practical move. Use Dashboard, runway, and Simulate to ask what changes if conditions get harder. Then choose one step that makes the plan less fragile.',
				},
				{
					type: 'key_takeaway',
					text: 'Macro money is not about knowing the future. It is about building a plan that can handle more than one future.',
				},
			],
			audioScript:
				'Macro money can make people smarter, but it can also make people anxious. The danger is thinking the goal is prediction. What will inflation do next? Will rates fall? Will housing crash? Will jobs weaken? Will the market rise? Will the currency lose value? These questions are understandable. But if your whole plan depends on getting them right, your plan is fragile.\n\nThe economy is complicated. Even experts disagree. Data changes. Policy changes. People behave in ways that models do not expect. Markets can move before the average person understands why. A forecast can sound confident and still be wrong. Macro money becomes unhealthy when you spend all your energy trying to know the future and none of your energy preparing for uncertainty.\n\nThe useful version is different. It says: stop pretending the environment is fixed. Build a plan that can handle more than one environment. If inflation stays high, what happens? If prices cool, what happens? If income gets shaky, what happens? If rates stay expensive, what happens? If housing does not get cheaper, what happens? If your industry slows down, what happens? These questions do not require perfect prediction. They require honest preparation.\n\nThink about two people watching the same recession headlines. One spends months scrolling videos, arguing online, waiting for the perfect signal, and making no practical change. The other checks runway, updates a resume, reduces one fragile payment, keeps skills warm, avoids taking on a stretched obligation, and tests an income option. The second person is not better at forecasting. They are better at preparing.\n\nThis is the heart of Macro money for Nisk. The product is not trying to turn users into economists or market timers. It is trying to help them connect big forces to their own numbers. If your rent rises, what happens? If debt gets more expensive, what happens? If your income pauses for one month, what happens? If groceries rise again, what happens? These are personal macro questions.\n\nThe blind spot is swinging too far in the other direction and ignoring macro completely. You do not need to obsess over headlines, but you should know what your life is exposed to. A person with variable debt should care about rates. A renter should care about housing pressure. A worker in a cyclical industry should care about job markets. A person with no runway should care about economic shocks.\n\nInside Nisk, this lesson points to a simple process. Look at Dashboard. Check runway. Run a scenario in Simulate. Pick one practical move. Maybe it is building one more month of runway. Maybe it is reducing a fragile payment. Maybe it is improving income options. Maybe it is not making a huge commitment until the base is stronger.\n\nMacro money is not about fear. It is about humility. You do not know the future. Build like you know that.',
			flashcards: [
				{
					id: 'school-macro-money-03-fc-1',
					role: 'main_idea',
					front: 'What is macro preparation?',
					back: 'Building a plan that can handle more than one economic future.',
				},
				{
					id: 'school-macro-money-03-fc-2',
					role: 'watch_out',
					front: 'What is the prediction trap?',
					back: 'Panic scrolling forecasts instead of improving your actual plan.',
				},
				{
					id: 'school-macro-money-03-fc-3',
					role: 'use_it',
					front: 'What practical move helps?',
					back: 'Check runway, test harder conditions, and reduce one fragile exposure.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your shock runway',
			},
			revision: 1,
		},
	],
};
